var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import StadiumIcon from "@mui/icons-material/Stadium";
import { Box, Button, Card, CardContent, CardHeader, IconButton, Stack, Step, StepLabel, Stepper, Tooltip, Typography, useMediaQuery, useTheme, } from "@mui/material";
import dayjs from "dayjs";
import React, { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useRetrieveDivisionsBySeasonId } from "../api/DivisionApiProvider";
import { useRetrieveLeague } from "../api/LeagueApiProvider";
import { useListLeagueHomeVenues } from "../api/LeagueVenueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { useCancelSeason, useDeleteSeason, useGenerateFixtures, useRetrieveSeason, useRetrieveSeasonConfig, useUpdateSeasonConfig, } from "../api/SeasonApiProvider";
import { UserContext } from "../auth/UserProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import SuccessSnackbar from "../utils/SuccessSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
import { ActiveSeason } from "./ActiveSeason";
import { DivisionManagement } from "./DivisionManagement";
import { ExclusionsStep } from "./ExclusionsStep";
import { FinalizeStep } from "./FinalizeStep";
import { FormatRoundsStep } from "./FormatRoundsStep";
import { MatchTimingStep } from "./MatchTimingStep";
import { ScheduleWindowStep, createDefaultDays } from "./ScheduleWindowStep";
import { SeasonStatusChip } from "./SeasonStatusChip";
import { VenuesStep } from "./VenuesStep";
var SETUP_STEPS = [
    {
        label: "Teams & Divisions",
        description: "Assign teams to divisions",
        icon: _jsx(GroupsIcon, {}),
    },
    {
        label: "Format & Rounds",
        description: "Season dates and match format",
        icon: _jsx(SettingsIcon, {}),
    },
    {
        label: "Venues",
        description: "Set up match locations",
        icon: _jsx(StadiumIcon, {}),
    },
    {
        label: "Match Timing",
        description: "Duration and rest periods",
        icon: _jsx(ScheduleIcon, {}),
    },
    {
        label: "Pitch Availability",
        description: "Days and times for each pitch",
        icon: _jsx(CalendarMonthIcon, {}),
    },
    {
        label: "Exclusions",
        description: "Holidays and blackouts",
        icon: _jsx(EventBusyIcon, {}),
    },
    {
        label: "Finalize",
        description: "Generate fixtures",
        icon: _jsx(CheckCircleIcon, {}),
    },
];
export var Season = function () {
    var _a, _b;
    var id = useNumericParams().id;
    var navigate = useNavigate();
    var user = useContext(UserContext).user;
    var _c = __read(useSearchParams(), 2), searchParams = _c[0], setSearchParams = _c[1];
    var theme = useTheme();
    var isMobile = useMediaQuery(theme.breakpoints.down("md"));
    var _d = __read(useState(0), 2), activeStep = _d[0], setActiveStep = _d[1];
    // Update both state and URL when changing steps
    var goToStep = function (step) {
        setActiveStep(step);
        setSearchParams(function (prev) {
            var next = new URLSearchParams(prev);
            next.set("step", String(step));
            return next;
        }, { replace: true });
    };
    var _e = __read(useState(false), 2), hasAutoAdvanced = _e[0], setHasAutoAdvanced = _e[1];
    var _f = __read(useState(false), 2), showDelete = _f[0], setShowDelete = _f[1];
    var _g = __read(useState(false), 2), deleting = _g[0], setDeleting = _g[1];
    var _h = __read(useState(false), 2), successSnackbarOpen = _h[0], setSuccessSnackbarOpen = _h[1];
    var _j = __read(useState(""), 2), successSnackbarMessage = _j[0], setSuccessSnackbarMessage = _j[1];
    var _k = __read(useState(false), 2), errorSnackbarOpen = _k[0], setErrorSnackbarOpen = _k[1];
    var _l = __read(useState(""), 2), errorSnackbarMessage = _l[0], setErrorSnackbarMessage = _l[1];
    var _m = __read(useState(false), 2), step1HasUnsavedChanges = _m[0], setStep1HasUnsavedChanges = _m[1];
    var _o = __read(useState(false), 2), isFormInitialized = _o[0], setIsFormInitialized = _o[1];
    // Fixture config form - single source of truth for all steps
    var formMethods = useForm({
        defaultValues: {},
    });
    var fixtureConfig = formMethods.watch();
    var _p = useRetrieveSeason({ id: id }), season = _p.data, isLoading = _p.isLoading, error = _p.error;
    var _q = useRetrieveLeague({
        id: season === null || season === void 0 ? void 0 : season.league,
        enabled: !!(season === null || season === void 0 ? void 0 : season.league),
    }), league = _q.data, leagueLoading = _q.isLoading;
    var _r = useRetrieveDivisionsBySeasonId({
        seasonId: season === null || season === void 0 ? void 0 : season.id,
        enabled: !!(season === null || season === void 0 ? void 0 : season.id),
    }), divisions = _r.data, divisionsLoading = _r.isLoading;
    var _s = useRetrieveUserLeaguePermissions({
        leagueId: season === null || season === void 0 ? void 0 : season.league,
    }), permissions = _s.data, permissionsLoading = _s.isLoading;
    var _t = (permissions || {}).canModify, canModify = _t === void 0 ? false : _t;
    var _u = useListLeagueHomeVenues({
        leagueId: season === null || season === void 0 ? void 0 : season.league,
        enabled: !!(season === null || season === void 0 ? void 0 : season.league) && !!user,
    }), leagueVenues = _u.data, leagueVenuesLoading = _u.isLoading;
    var _v = useRetrieveSeasonConfig({
        seasonId: season === null || season === void 0 ? void 0 : season.id,
        enabled: !!(season === null || season === void 0 ? void 0 : season.id) && canModify,
    }), seasonConfig = _v.data, seasonConfigLoading = _v.isLoading, seasonConfigError = _v.error;
    var cancelMutation = useCancelSeason();
    var updateConfigMutation = useUpdateSeasonConfig({
        seasonId: id,
    });
    var deleteMutation = useDeleteSeason({
        onMutate: function () { return setDeleting(true); },
        onSuccess: function () { return navigate("/leagues/".concat(season === null || season === void 0 ? void 0 : season.league, "/")); },
        onError: function () { return setDeleting(false); },
    });
    var generateMutation = useGenerateFixtures({
        seasonId: id,
        onSuccess: function (response) {
            var totalMatches = response.data.totalMatches;
            setSuccessSnackbarMessage(totalMatches === 0
                ? "Season activated! You can now create matches manually."
                : "Successfully generated ".concat(totalMatches, " fixtures!"));
            setSuccessSnackbarOpen(true);
            // Navigate to the season page (will show active state)
            navigate("/leagues/".concat(season === null || season === void 0 ? void 0 : season.league, "/seasons/").concat(id));
        },
        onError: function (error) {
            var _a, _b;
            var axiosError = error;
            var message = ((_b = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) ||
                "Failed to generate fixtures. Please try again.";
            setErrorSnackbarMessage(message);
            setErrorSnackbarOpen(true);
        },
    });
    // Initialize fixtureConfig form from loaded seasonConfig
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!seasonConfig || isFormInitialized)
            return;
        var newConfig = {};
        // Format & Rounds (now includes dates)
        if (seasonConfig.startDate || seasonConfig.endMode) {
            // Map endMode from API to scheduleMode
            var scheduleMode = seasonConfig.endMode === "date"
                ? "fillDates"
                : seasonConfig.endMode === "manual"
                    ? "manual"
                    : "roundRobin";
            newConfig.formatRounds = {
                scheduleMode: scheduleMode,
                startDate: seasonConfig.startDate
                    ? new Date(seasonConfig.startDate)
                    : new Date(),
                endDate: seasonConfig.endDate ? new Date(seasonConfig.endDate) : null,
                matchesPerPairing: (_a = seasonConfig.matchesPerPairing) !== null && _a !== void 0 ? _a : 1,
                randomizeOrder: (_b = seasonConfig.randomizeOrder) !== null && _b !== void 0 ? _b : false,
                gameWeekStartDay: ((_c = seasonConfig.gameWeekStartDay) !== null && _c !== void 0 ? _c : 0),
                divisionPlayoffs: (_e = (_d = seasonConfig.divisionPlayoffs) === null || _d === void 0 ? void 0 : _d.map(function (dp) {
                    var _a;
                    return ({
                        divisionId: dp.division,
                        enabled: (_a = dp.enabled) !== null && _a !== void 0 ? _a : false,
                        teamCount: dp.teamCount,
                    });
                })) !== null && _e !== void 0 ? _e : [],
            };
        }
        // Venues - API returns venues with nested pitches
        if (seasonConfig.venueMode) {
            var venueMode = seasonConfig.venueMode === "home_away" ? "homeAway" : "neutral";
            // Extract IDs and pitch info from the nested structure
            var selectedVenuesData = (_f = seasonConfig.selectedVenues) !== null && _f !== void 0 ? _f : [];
            var selectedVenueIds = selectedVenuesData.map(function (v) { return v.id; });
            var selectedPitchIds_1 = [];
            var selectedPitches_1 = [];
            selectedVenuesData.forEach(function (venue) {
                venue.pitches.forEach(function (pitch) {
                    selectedPitchIds_1.push(pitch.id);
                    selectedPitches_1.push({
                        pitchId: pitch.id,
                        pitchName: pitch.name,
                        venueId: venue.id,
                        venueName: venue.name,
                    });
                });
            });
            newConfig.venues = {
                venueMode: venueMode,
                selectedVenueIds: selectedVenueIds,
                selectedPitchIds: selectedPitchIds_1,
                selectedPitches: selectedPitches_1,
            };
        }
        // Match Timing
        if (seasonConfig.matchDuration !== null &&
            seasonConfig.matchDuration !== undefined) {
            newConfig.matchTiming = {
                matchDuration: seasonConfig.matchDuration,
                halfTimeBreak: (_g = seasonConfig.halfTimeBreak) !== null && _g !== void 0 ? _g : 0,
                warmupTime: (_h = seasonConfig.warmupTime) !== null && _h !== void 0 ? _h : 5,
                timezone: seasonConfig.timezone ||
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
        }
        // Schedule Window (pitch schedules only)
        if (seasonConfig.pitchAvailability &&
            seasonConfig.pitchAvailability.length > 0) {
            var dayKeys_1 = [
                "monday",
                "tuesday",
                "wednesday",
                "thursday",
                "friday",
                "saturday",
                "sunday",
            ];
            newConfig.scheduleWindow = {
                pitchSchedules: seasonConfig.pitchAvailability.reduce(function (acc, pa) {
                    var _a, _b;
                    // Use -venueId for venue-only entries (no pitch), matching stepContext convention
                    var pitchId = (_a = pa.pitch) !== null && _a !== void 0 ? _a : (pa.venue ? -pa.venue : 0);
                    var venueId = (_b = pa.venue) !== null && _b !== void 0 ? _b : 0;
                    // Group by pitch
                    var existing = acc.find(function (ps) { return ps.pitchId === pitchId; });
                    var dayKey = dayKeys_1[pa.dayOfWeek];
                    if (existing) {
                        if (dayKey) {
                            existing.days[dayKey] = {
                                enabled: true,
                                timeWindows: [
                                    { startTime: pa.startTime, endTime: pa.endTime },
                                ],
                            };
                        }
                    }
                    else {
                        var days = createDefaultDays();
                        if (dayKey) {
                            days[dayKey] = {
                                enabled: true,
                                timeWindows: [
                                    { startTime: pa.startTime, endTime: pa.endTime },
                                ],
                            };
                        }
                        acc.push({
                            pitchId: pitchId,
                            pitchName: pitchId === 0
                                ? "All Venues"
                                : pitchId < 0
                                    ? "Venue ".concat(venueId)
                                    : "Pitch ".concat(pitchId),
                            venueId: venueId,
                            venueName: "",
                            days: days,
                        });
                    }
                    return acc;
                }, []),
            };
        }
        // Exclusions
        if (seasonConfig.exclusions && seasonConfig.exclusions.length > 0) {
            newConfig.exclusions = {
                excludedDates: [],
                blackoutPeriods: seasonConfig.exclusions.map(function (e) {
                    var _a;
                    return ({
                        startDate: new Date(e.startDate),
                        endDate: new Date(e.endDate),
                        reason: (_a = e.reason) !== null && _a !== void 0 ? _a : "",
                    });
                }),
                skipHolidays: false,
                countryCode: "US",
            };
        }
        formMethods.reset(newConfig);
        setIsFormInitialized(true);
    }, [seasonConfig, isFormInitialized, formMethods]);
    // Auto-advance to the first incomplete step when page loads (based on backend data only)
    useEffect(function () {
        // Don't auto-advance if we've already done it
        if (hasAutoAdvanced)
            return;
        // Wait for all data to load
        if (isLoading || divisionsLoading || seasonConfigLoading)
            return;
        // Mark as auto-advanced FIRST to prevent re-runs
        setHasAutoAdvanced(true);
        // If a step was explicitly requested via query param (e.g. returnTo from team creation),
        // honor it instead of auto-advancing
        var stepParam = searchParams.get("step");
        if (stepParam !== null) {
            var requestedStep = parseInt(stepParam, 10);
            if (!isNaN(requestedStep) &&
                requestedStep >= 0 &&
                requestedStep < SETUP_STEPS.length) {
                setActiveStep(requestedStep);
                return;
            }
        }
        // Find the first incomplete step based on BACKEND data (seasonConfig), not form state
        var checkStepComplete = function (stepIndex) {
            var _a, _b, _c, _d;
            switch (stepIndex) {
                case 0: {
                    // Step 1: Teams & Divisions
                    if (!divisions || divisions.length === 0)
                        return false;
                    return divisions.every(function (d) { return d.teams && d.teams.length >= 2; });
                }
                case 1: {
                    // Step 2: Format & Rounds - check backend seasonConfig
                    if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "manual")
                        return true;
                    if (!(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.startDate))
                        return false;
                    if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "date" && !(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endDate))
                        return false;
                    if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) !== "date" &&
                        !(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.matchesPerPairing))
                        return false;
                    return true;
                }
                case 2: {
                    // Step 3: Venues - auto-complete for manual mode
                    if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "manual")
                        return true;
                    if (!(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.venueMode))
                        return false;
                    if (seasonConfig.venueMode === "home_away")
                        return true;
                    return ((_b = (_a = seasonConfig.selectedVenues) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
                }
                case 3: {
                    // Step 4: Match Timing - auto-complete for manual mode
                    if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "manual")
                        return true;
                    return ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.matchDuration) !== undefined &&
                        (seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.matchDuration) !== null);
                }
                case 4: {
                    // Step 5: Schedule Window - auto-complete for manual mode
                    if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "manual")
                        return true;
                    return ((_d = (_c = seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.pitchAvailability) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0;
                }
                case 5: {
                    // Step 6: Exclusions - auto-complete for manual mode
                    if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "manual")
                        return true;
                    return checkStepComplete(4);
                }
                default:
                    return false;
            }
        };
        // Find first incomplete step
        var targetStep = 0;
        for (var i = 0; i < SETUP_STEPS.length; i++) {
            if (!checkStepComplete(i)) {
                targetStep = i;
                break;
            }
            // If all steps are complete, go to the finalize step
            if (i === SETUP_STEPS.length - 1) {
                targetStep = SETUP_STEPS.length - 1;
            }
        }
        goToStep(targetStep);
    }, [
        hasAutoAdvanced,
        isLoading,
        divisionsLoading,
        seasonConfigLoading,
        divisions,
        seasonConfig,
        searchParams,
    ]);
    var canModifyStructure = function (status) {
        return status === "draft";
    };
    // Check if Step 1 (Teams & Divisions) is complete
    var isStep1Complete = function () {
        if (!divisions || divisions.length === 0)
            return false;
        return divisions.every(function (division) { return division.teams && division.teams.length >= 2; });
    };
    // Check if Step 2 (Format & Rounds) is complete (based on SAVED backend data)
    var isStep2Complete = function () {
        if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "manual")
            return true;
        if (!(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.startDate))
            return false;
        if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) === "date" && !(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endDate))
            return false;
        if ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.endMode) !== "date" && !(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.matchesPerPairing))
            return false;
        return true;
    };
    // Check if Step 2 form is valid (for enabling Next button)
    var isStep2Valid = function () {
        var formatRounds = fixtureConfig.formatRounds;
        if (!formatRounds)
            return false;
        if (formatRounds.scheduleMode === "manual")
            return true;
        if (!formatRounds.startDate)
            return false;
        if (formatRounds.scheduleMode === "fillDates" && !formatRounds.endDate)
            return false;
        if (formatRounds.scheduleMode === "roundRobin" &&
            !formatRounds.matchesPerPairing)
            return false;
        return true;
    };
    // Check if Step 3 (Venues) is complete (based on SAVED backend data)
    var isStep3Complete = function () {
        var _a, _b, _c;
        if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual")
            return true;
        if (!(seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.venueMode))
            return false;
        if (seasonConfig.venueMode === "home_away")
            return true;
        return ((_c = (_b = seasonConfig.selectedVenues) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0;
    };
    // Check if Step 3 form is valid (for enabling Next button)
    var isStep3Valid = function () {
        var _a, _b, _c;
        if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual")
            return true;
        var venues = fixtureConfig.venues;
        if (!venues || !venues.venueMode)
            return false;
        if (venues.venueMode === "homeAway")
            return true;
        return ((_c = (_b = venues.selectedVenueIds) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0;
    };
    // Check if Step 4 (Match Timing) is complete (based on SAVED backend data)
    var isStep4Complete = function () {
        var _a;
        if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual")
            return true;
        return ((seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.matchDuration) !== undefined &&
            (seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.matchDuration) !== null);
    };
    // Check if Step 4 form is valid (for enabling Next button)
    var isStep4Valid = function () {
        var _a, _b;
        if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual")
            return true;
        return ((_b = fixtureConfig.matchTiming) === null || _b === void 0 ? void 0 : _b.matchDuration) !== undefined;
    };
    // Check if Step 5 (Schedule Window) is complete (based on SAVED backend data)
    var isStep5Complete = function () {
        var _a, _b, _c;
        if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual")
            return true;
        return ((_c = (_b = seasonConfig === null || seasonConfig === void 0 ? void 0 : seasonConfig.pitchAvailability) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0;
    };
    // Check if Step 5 form is valid (for enabling Next button)
    var isStep5Valid = function () {
        var _a;
        if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual")
            return true;
        var scheduleWindow = fixtureConfig.scheduleWindow;
        if (!scheduleWindow)
            return false;
        return scheduleWindow.pitchSchedules.some(function (ps) {
            return Object.values(ps.days).some(function (day) { return day.enabled; });
        });
    };
    // Check if Step 6 (Exclusions) is complete
    var isStep6Complete = function () {
        var _a;
        if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual")
            return true;
        // Exclusions step is always valid (optional to add exclusions)
        return isStep5Complete(); // Just needs previous step to be complete
    };
    // Check if a specific step is complete (for stepper checkmarks - based on SAVED data)
    var isStepComplete = function (stepIndex) {
        switch (stepIndex) {
            case 0:
                return isStep1Complete();
            case 1:
                return isStep2Complete();
            case 2:
                return isStep3Complete();
            case 3:
                return isStep4Complete();
            case 4:
                return isStep5Complete();
            case 5:
                return isStep6Complete();
            default:
                return false;
        }
    };
    // Check if user can proceed from a step (for enabling Next button - based on FORM data)
    var canProceedFromStep = function (stepIndex) {
        switch (stepIndex) {
            case 0:
                return isStep1Complete(); // Step 1 has no form, uses divisions directly
            case 1:
                return isStep2Valid();
            case 2:
                return isStep3Valid();
            case 3:
                return isStep4Valid();
            case 4:
                return isStep5Valid();
            case 5:
                return true; // Exclusions are optional
            default:
                return false;
        }
    };
    // Determine if a step can be navigated to
    var canNavigateToStep = function (stepIndex) {
        // Can always go back to previous steps
        if (stepIndex <= activeStep)
            return true;
        // Can only go forward if all previous steps are complete
        for (var i = 0; i < stepIndex; i++) {
            if (!isStepComplete(i))
                return false;
        }
        // Additional check for step 1: no unsaved changes
        if (activeStep === 0 && step1HasUnsavedChanges)
            return false;
        return true;
    };
    var handleStepClick = function (stepIndex) {
        if (canNavigateToStep(stepIndex)) {
            goToStep(stepIndex);
        }
    };
    // Build config payload for current step
    var buildConfigPayload = function () {
        var _a, _b, _c, _d, _e;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var payload = {};
        // Always send timezone so it's set from the first config save
        payload.timezone =
            ((_a = fixtureConfig.matchTiming) === null || _a === void 0 ? void 0 : _a.timezone) ||
                Intl.DateTimeFormat().resolvedOptions().timeZone;
        switch (activeStep) {
            case 1: {
                // Format & Rounds (now includes dates)
                var fr = fixtureConfig.formatRounds;
                if (fr) {
                    payload.startDate = (_c = (_b = fr.startDate) === null || _b === void 0 ? void 0 : _b.toISOString().split("T")[0]) !== null && _c !== void 0 ? _c : null;
                    // Map scheduleMode to endMode for API
                    if (fr.scheduleMode === "manual") {
                        payload.endMode = "manual";
                        payload.endDate = null;
                    }
                    else if (fr.scheduleMode === "fillDates") {
                        payload.endMode = "date";
                        payload.endDate = (_e = (_d = fr.endDate) === null || _d === void 0 ? void 0 : _d.toISOString().split("T")[0]) !== null && _e !== void 0 ? _e : null;
                    }
                    else {
                        payload.endMode = "rounds";
                        payload.endDate = null;
                        payload.matchesPerPairing = fr.matchesPerPairing;
                    }
                    if (fr.scheduleMode !== "manual") {
                        payload.randomizeOrder = fr.randomizeOrder;
                        payload.gameWeekStartDay = fr.gameWeekStartDay;
                        payload.divisionPlayoffs = fr.divisionPlayoffs
                            .filter(function (dp) { return dp.enabled; })
                            .map(function (dp) { return ({
                            division: dp.divisionId,
                            enabled: dp.enabled,
                            teamCount: dp.teamCount,
                        }); });
                    }
                }
                break;
            }
            case 2: {
                // Venues
                var v = fixtureConfig.venues;
                if (v) {
                    if (v.venueMode) {
                        payload.venueMode =
                            v.venueMode === "homeAway" ? "home_away" : "neutral";
                    }
                    payload.selectedVenueIds = v.selectedVenueIds;
                    payload.selectedPitchIds = v.selectedPitchIds;
                }
                break;
            }
            case 3: {
                // Match Timing
                var mt = fixtureConfig.matchTiming;
                if (mt) {
                    payload.matchDuration = mt.matchDuration;
                    payload.halfTimeBreak = mt.halfTimeBreak;
                    payload.warmupTime = mt.warmupTime;
                }
                break;
            }
            case 4: {
                // Schedule Window (pitch schedules only, dates moved to step 1)
                var sw = fixtureConfig.scheduleWindow;
                if (sw) {
                    // Convert pitch schedules to pitch_availability format
                    var pitchAvailability_1 = [];
                    var dayKeyToNumber_1 = {
                        monday: 0,
                        tuesday: 1,
                        wednesday: 2,
                        thursday: 3,
                        friday: 4,
                        saturday: 5,
                        sunday: 6,
                    };
                    sw.pitchSchedules.forEach(function (ps) {
                        Object.entries(ps.days).forEach(function (_a) {
                            var _b;
                            var _c = __read(_a, 2), dayKey = _c[0], dayConfig = _c[1];
                            if (dayConfig.enabled && dayConfig.timeWindows[0]) {
                                var entry = {
                                    dayOfWeek: (_b = dayKeyToNumber_1[dayKey]) !== null && _b !== void 0 ? _b : 0,
                                    startTime: dayConfig.timeWindows[0].startTime,
                                    endTime: dayConfig.timeWindows[0].endTime,
                                };
                                // Venue-only entries have negative pitchId (placeholder)
                                entry.venue = ps.venueId > 0 ? ps.venueId : undefined;
                                entry.pitch = ps.pitchId > 0 ? ps.pitchId : undefined;
                                pitchAvailability_1.push(entry);
                            }
                        });
                    });
                    payload.pitchAvailability = pitchAvailability_1;
                }
                break;
            }
            case 5: {
                // Exclusions
                var ex = fixtureConfig.exclusions;
                if (ex) {
                    var exclusions_1 = [];
                    // Add blackout periods
                    ex.blackoutPeriods.forEach(function (bp) {
                        var _a, _b;
                        exclusions_1.push({
                            startDate: (_a = bp.startDate.toISOString().split("T")[0]) !== null && _a !== void 0 ? _a : "",
                            endDate: (_b = bp.endDate.toISOString().split("T")[0]) !== null && _b !== void 0 ? _b : "",
                            reason: bp.reason,
                        });
                    });
                    // Add excluded dates as single-day exclusions
                    ex.excludedDates.forEach(function (ed) {
                        var _a;
                        var dateStr = (_a = ed.date.toISOString().split("T")[0]) !== null && _a !== void 0 ? _a : "";
                        exclusions_1.push({
                            startDate: dateStr,
                            endDate: dateStr,
                            reason: ed.reason,
                        });
                    });
                    payload.exclusions = exclusions_1;
                }
                break;
            }
            default:
                break;
        }
        return payload;
    };
    var handleNext = function () {
        var _a;
        if (activeStep < SETUP_STEPS.length - 1) {
            var isManual_1 = ((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual";
            // Steps 1-5 save config (step 0 is team assignment, step 6 is finalize)
            if (activeStep >= 1 && activeStep <= 5) {
                var payload = buildConfigPayload();
                // Only patch if there's something to save
                if (Object.keys(payload).length > 0) {
                    updateConfigMutation.mutate(payload, {
                        onSuccess: function () {
                            // Manual mode: skip from step 1 directly to Finalize (step 6)
                            if (isManual_1 && activeStep === 1) {
                                goToStep(6);
                            }
                            else {
                                goToStep(activeStep + 1);
                            }
                        },
                    });
                    return;
                }
            }
            // Manual mode: skip from step 1 directly to Finalize (step 6)
            if (isManual_1 && activeStep === 1) {
                goToStep(6);
            }
            else {
                goToStep(activeStep + 1);
            }
        }
    };
    var handleBack = function () {
        var _a;
        if (activeStep > 0) {
            // Manual mode: jump back from Finalize (step 6) to Format (step 1)
            if (((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.scheduleMode) === "manual" &&
                activeStep === 6) {
                goToStep(1);
            }
            else {
                goToStep(activeStep - 1);
            }
        }
    };
    // Build shared context for all step components
    var stepContext = React.useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g;
        var teamCount = (divisions === null || divisions === void 0 ? void 0 : divisions.reduce(function (sum, d) { var _a; return sum + (((_a = d.teams) === null || _a === void 0 ? void 0 : _a.length) || 0); }, 0)) || 0;
        var gamesPerRound = Math.floor(teamCount / 2);
        var matchesPerPairing = ((_a = fixtureConfig.formatRounds) === null || _a === void 0 ? void 0 : _a.matchesPerPairing) || 1;
        var totalRounds = matchesPerPairing * (teamCount > 0 ? teamCount - 1 : 0);
        var pitchCount = ((_c = (_b = fixtureConfig.venues) === null || _b === void 0 ? void 0 : _b.selectedPitchIds) === null || _c === void 0 ? void 0 : _c.length) || 0;
        // Build pitch info from selected pitches/venues
        var selectedPitchesFromConfig = ((_d = fixtureConfig.venues) === null || _d === void 0 ? void 0 : _d.selectedPitches) || [];
        var venueIds = ((_e = fixtureConfig.venues) === null || _e === void 0 ? void 0 : _e.selectedVenueIds) || [];
        var pitches = [];
        var pitchesByVenue = new Map();
        selectedPitchesFromConfig.forEach(function (p) {
            if (!pitchesByVenue.has(p.venueId)) {
                pitchesByVenue.set(p.venueId, []);
            }
            pitchesByVenue.get(p.venueId).push(p);
        });
        venueIds.forEach(function (venueId) {
            var _a;
            var venue = leagueVenues === null || leagueVenues === void 0 ? void 0 : leagueVenues.find(function (lv) { var _a; return ((_a = lv.venue) === null || _a === void 0 ? void 0 : _a.id) === venueId; });
            var venueName = ((_a = venue === null || venue === void 0 ? void 0 : venue.venue) === null || _a === void 0 ? void 0 : _a.name) || "Venue ".concat(venueId);
            var venuePitches = pitchesByVenue.get(venueId);
            if (venuePitches && venuePitches.length > 0) {
                venuePitches.forEach(function (p) {
                    pitches.push({
                        id: p.pitchId,
                        name: p.pitchName,
                        venueId: venueId,
                        venueName: venueName,
                    });
                });
            }
            else {
                pitches.push({ id: -venueId, name: venueName, venueId: venueId, venueName: "" });
            }
        });
        // Calculate estimated end date for rounds mode
        var formatRounds = fixtureConfig.formatRounds;
        var scheduleWindow = fixtureConfig.scheduleWindow;
        var slotDuration = fixtureConfig.matchTiming
            ? fixtureConfig.matchTiming.matchDuration +
                fixtureConfig.matchTiming.halfTimeBreak +
                fixtureConfig.matchTiming.warmupTime
            : 45;
        var totalSlotsPerWeek = ((_f = scheduleWindow === null || scheduleWindow === void 0 ? void 0 : scheduleWindow.pitchSchedules) === null || _f === void 0 ? void 0 : _f.reduce(function (sum, ps) {
            return (sum +
                Object.values(ps.days).reduce(function (daySum, day) {
                    var _a, _b, _c, _d;
                    if (!day.enabled || !day.timeWindows[0])
                        return daySum;
                    var tw = day.timeWindows[0];
                    var startParts = tw.startTime.split(":").map(Number);
                    var endParts = tw.endTime.split(":").map(Number);
                    var minutes = ((_a = endParts[0]) !== null && _a !== void 0 ? _a : 0) * 60 +
                        ((_b = endParts[1]) !== null && _b !== void 0 ? _b : 0) -
                        (((_c = startParts[0]) !== null && _c !== void 0 ? _c : 0) * 60 + ((_d = startParts[1]) !== null && _d !== void 0 ? _d : 0));
                    return daySum + Math.floor(minutes / slotDuration);
                }, 0));
        }, 0)) || 0;
        var totalGames = gamesPerRound * totalRounds;
        var weeksNeeded = totalSlotsPerWeek > 0 ? Math.ceil(totalGames / totalSlotsPerWeek) : 12;
        var estimatedEndDate = (formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.startDate) && (formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.scheduleMode) === "roundRobin"
            ? new Date(formatRounds.startDate.getTime() +
                weeksNeeded * 7 * 24 * 60 * 60 * 1000)
            : null;
        return {
            divisions: divisions || [],
            leagueVenues: leagueVenues || [],
            leagueId: season === null || season === void 0 ? void 0 : season.league,
            seasonId: season === null || season === void 0 ? void 0 : season.id,
            teamCount: teamCount,
            gamesPerRound: gamesPerRound,
            totalRounds: totalRounds,
            pitchCount: pitchCount,
            pitches: pitches,
            startDate: formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.startDate,
            endDate: formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.endDate,
            endMode: ((formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.scheduleMode) === "fillDates"
                ? "date"
                : (formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.scheduleMode) === "manual"
                    ? "manual"
                    : "rounds"),
            estimatedEndDate: estimatedEndDate,
            gameWeekStartDay: (_g = formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.gameWeekStartDay) !== null && _g !== void 0 ? _g : 0,
        };
    }, [divisions, leagueVenues, season === null || season === void 0 ? void 0 : season.league, fixtureConfig]);
    // Render step content
    var renderStepContent = function () {
        switch (activeStep) {
            case 0:
                return (_jsx(DivisionManagement, { season: season, canModify: (permissions === null || permissions === void 0 ? void 0 : permissions.canModify) || false, showActions: true, onHasChangesChange: setStep1HasUnsavedChanges, onDeleteSuccess: function (message) {
                        setSuccessSnackbarMessage(message);
                        setSuccessSnackbarOpen(true);
                    }, onDeleteError: function (message) {
                        setErrorSnackbarMessage(message);
                        setErrorSnackbarOpen(true);
                    } }));
            case 1:
                return _jsx(FormatRoundsStep, { context: stepContext });
            case 2:
                return _jsx(VenuesStep, { context: stepContext });
            case 3:
                return _jsx(MatchTimingStep, { context: stepContext });
            case 4:
                return _jsx(ScheduleWindowStep, { context: stepContext });
            case 5:
                return _jsx(ExclusionsStep, { context: stepContext });
            case 6: {
                var teamCount = (divisions === null || divisions === void 0 ? void 0 : divisions.reduce(function (sum, d) { var _a; return sum + (((_a = d.teams) === null || _a === void 0 ? void 0 : _a.length) || 0); }, 0)) || 0;
                var divisionCount = (divisions === null || divisions === void 0 ? void 0 : divisions.length) || 0;
                var handleGenerate = function () {
                    generateMutation.mutate();
                };
                return (_jsx(FinalizeStep, { teamCount: teamCount, divisionCount: divisionCount, formatRounds: fixtureConfig.formatRounds, venues: fixtureConfig.venues, matchTiming: fixtureConfig.matchTiming, scheduleWindow: fixtureConfig.scheduleWindow, exclusions: fixtureConfig.exclusions, onGenerate: handleGenerate, isGenerating: generateMutation.isLoading, generateError: null }));
            }
            default: {
                var step = SETUP_STEPS[activeStep];
                return (_jsxs(Box, { sx: { p: 4, textAlign: "center" }, children: [_jsx(Typography, { variant: "h6", color: "text.secondary", gutterBottom: true, children: step === null || step === void 0 ? void 0 : step.label }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Coming soon..." })] }));
            }
        }
    };
    if (!id) {
        return _jsx(ErrorView, { error: new Error("Missing season id") });
    }
    // If retrieving the season is forbidden, show Forbidden
    if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 403) {
        return _jsx(Forbidden, {});
    }
    if (isLoading ||
        deleting ||
        permissionsLoading ||
        leagueLoading ||
        divisionsLoading ||
        (leagueVenuesLoading && !!user) ||
        (seasonConfigLoading && canModify)) {
        return _jsx(Loading, {});
    }
    if (error || !season || (canModify && seasonConfigError)) {
        return _jsx(ErrorView, { error: error || new Error("Failed to load season") });
    }
    if (season.status !== "draft") {
        return (_jsx(ActiveSeason, { season: season, league: league, permissions: permissions, onCancel: function () { return cancelMutation.mutate({ id: season.id }); }, isCancelling: cancelMutation.isLoading }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(SuccessSnackbar, { open: successSnackbarOpen, message: successSnackbarMessage, onClose: function () {
                    setSuccessSnackbarOpen(false);
                    setSuccessSnackbarMessage("");
                } }), _jsx(ErrorSnackbar, { open: deleteMutation.isError ||
                    updateConfigMutation.isError ||
                    errorSnackbarOpen, message: deleteMutation.isError
                    ? "Failed to delete season. Please try again."
                    : updateConfigMutation.isError
                        ? "Failed to save configuration. Please try again."
                        : errorSnackbarMessage, onClose: function () {
                    setErrorSnackbarOpen(false);
                    setErrorSnackbarMessage("");
                    updateConfigMutation.reset();
                } }), _jsx(DeleteDialog, { open: showDelete, title: "Delete ".concat(season.name, "?"), onCancel: function () { return setShowDelete(false); }, onConfirm: function () {
                    return deleteMutation.mutate({
                        id: season.id,
                        leagueId: season.league,
                    });
                } }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Stack, { spacing: 1, children: [league && (_jsx(Link, { to: "/leagues/".concat(season.league, "/"), style: { textDecoration: "none" }, children: _jsx(Typography, { variant: "body2", color: "primary.main", children: league.name }) })), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [_jsx(Typography, { variant: "h5", children: season.name }), _jsx(SeasonStatusChip, { season: season })] })] }), subheader: ((_b = fixtureConfig.formatRounds) === null || _b === void 0 ? void 0 : _b.startDate) ? (_jsxs(Typography, { variant: "caption", fontStyle: "italic", children: ["Starts", " ", dayjs(fixtureConfig.formatRounds.startDate).format("MMM D, YYYY")] })) : undefined, action: _jsx(Stack, { direction: "row", spacing: 1, children: (permissions === null || permissions === void 0 ? void 0 : permissions.canModify) && canModifyStructure(season.status) && (_jsxs(_Fragment, { children: [_jsx(Link, { to: "/leagues/".concat(season.league, "/seasons/").concat(season.id, "/edit"), children: _jsx(IconButton, { "aria-label": "edit", children: _jsx(EditIcon, {}) }) }), _jsx(IconButton, { "aria-label": "delete", onClick: function () { return setShowDelete(true); }, children: _jsx(DeleteIcon, {}) })] })) }) }), season.description && (_jsx(CardContent, { children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: season.description }) }))] }), season.status === "draft" && (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Box, { sx: { width: "100%", mb: 4 }, children: _jsx(Stepper, { activeStep: activeStep, alternativeLabel: !isMobile, orientation: isMobile ? "vertical" : "horizontal", sx: {
                                    "& .MuiStepLabel-root": {
                                        cursor: "pointer",
                                    },
                                    "& .Mui-active .MuiStepLabel-label": {
                                        fontWeight: 700,
                                        color: "primary.main",
                                    },
                                    "& .Mui-active .MuiStepIcon-root": {
                                        fontSize: "1.75rem",
                                        color: "primary.main",
                                    },
                                    "& .Mui-completed .MuiStepIcon-root": {
                                        color: "success.main",
                                    },
                                }, children: SETUP_STEPS.map(function (step, index) { return (_jsx(Step, { completed: isStepComplete(index), children: _jsx(StepLabel, { onClick: function () { return handleStepClick(index); }, optional: !isMobile ? (_jsx(Typography, { variant: "caption", children: step.description })) : undefined, sx: {
                                            "& .MuiStepLabel-label": {
                                                cursor: canNavigateToStep(index)
                                                    ? "pointer"
                                                    : "default",
                                                opacity: canNavigateToStep(index) ? 1 : 0.5,
                                            },
                                        }, children: step.label }) }, step.label)); }) }) }), _jsx(FormProvider, __assign({}, formMethods, { children: _jsx(Box, { sx: { minHeight: 300 }, children: renderStepContent() }) })), _jsxs(Box, { sx: {
                                display: "flex",
                                justifyContent: "space-between",
                                pt: 3,
                                borderTop: 1,
                                borderColor: "divider",
                                mt: 3,
                            }, children: [_jsx(Button, { disabled: activeStep === 0, onClick: handleBack, variant: "outlined", children: "Back" }), _jsx(Stack, { direction: "row", spacing: 2, children: activeStep < SETUP_STEPS.length - 1 && (_jsx(Tooltip, { title: activeStep === 0 && step1HasUnsavedChanges
                                            ? "Save your team assignments before continuing"
                                            : !canProceedFromStep(activeStep)
                                                ? activeStep === 0
                                                    ? "All divisions need at least 2 teams"
                                                    : "Complete this step before continuing"
                                                : "", children: _jsx("span", { children: _jsx(Button, { variant: "contained", onClick: handleNext, disabled: !canProceedFromStep(activeStep) ||
                                                    (activeStep === 0 && step1HasUnsavedChanges) ||
                                                    updateConfigMutation.isLoading, children: updateConfigMutation.isLoading ? "Saving..." : "Next" }) }) })) })] })] }) }))] }));
};
